import type { HighchartsReactRefObject } from 'highcharts-react-official';
import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Keeps a Highcharts chart sized to its container so it fills the available height and reflows when the
 * container becomes visible or resizes (e.g., when a dialog opens). Feed the returned `chartHeight` into
 * `chart.height` and attach `containerRef`/`chartRef` to the wrapping div and `HighchartsReact`. The
 * measured `containerWidth` is exposed so callers can size a horizontally scrollable plot area.
 */
export const useLiquidityChartAutoSize = (initialHeight: number) => {
    const chartRef = useRef<HighchartsReactRefObject | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [chartHeight, setChartHeight] = useState(initialHeight);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        const syncChartSize = () => {
            const height = container.clientHeight;

            setChartHeight(height);
            setContainerWidth(container.clientWidth);
            chartRef.current?.chart?.setSize(undefined, height, false);
            chartRef.current?.chart?.reflow();
        };

        syncChartSize();

        const observer = new ResizeObserver(syncChartSize);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    return { chartRef, containerRef, chartHeight, containerWidth };
};
